import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth/auth.service';
// If you keep API URLs in a helper, add a function like this there:
import { getFtdTokenUrl } from '../shared/utils/api-urls.utils';

declare global {
  interface Window {
    postData?: () => Promise<{ payment_token_id?: string }>;
  }
}

@Injectable({ providedIn: 'root' })
export class IpospayService {
  private loadPromise: Promise<void> | null = null;
  private scriptId = 'ftd-script';
  private ftdSrc = 'https://payment.ipospays.tech/ftd/v1/freedomtodesign.js';

  // prefer `inject()` style to match your other services
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  /** Ensure the FTD script is loaded with a valid token from backend */
  async ensureLoaded(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      // 1) get short-lived token from your backend with auth header
      const tokenResp = await firstValueFrom(
        this.http.get<{ token: string }>(getFtdTokenUrl(), {
          headers: this.authHeaders(),
          // uncomment if you use cookie-based auth
          // withCredentials: true
        })
      );
      const token = tokenResp?.token;
      if (!token) throw new Error('No security token returned from backend');

      // 2) remove any previous script to avoid stale tokens
      const existing = document.getElementById(this.scriptId);
      if (existing) existing.remove();

      // 3) inject script with token
      await this.injectScriptWithToken(token);
    })();

    return this.loadPromise;
  }

  private authHeaders(): HttpHeaders {
    const token = this.auth.token;
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

private injectScriptWithToken(token: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // remove any previous FTD tag by the id the lib expects
    const old = document.getElementById('ftd');
    if (old) old.remove();

    const s = document.createElement('script');
    s.id = 'ftd'; // <-- IMPORTANT: the library looks for this id
    s.src = this.ftdSrc;
    s.defer = true;
    s.setAttribute('security_key', token); // <-- attribute the lib reads
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load FTD script'));
    document.head.appendChild(s);
  });
}

}
